import { translate } from 'support/translate';
import ParentalPin, {
  ParentalPinTemplateSpec,
} from 'components/widgets/parental-pin/ParentalPin';
import { AppData, Lightning } from '@lightningjs/sdk';
import ResetPinButton from 'components/widgets/parental-pin/ResetPinButton';
import { constants } from 'aliases';

export interface EnterParentalPinTemplateSpec extends ParentalPinTemplateSpec {
  Content: {
    ResetPin: typeof ResetPinButton;
  } & ParentalPinTemplateSpec['Content'];
}

const RESET_PIN_MARGIN_TOP = 48;

export default class EnterParentalPin extends ParentalPin<EnterParentalPinTemplateSpec> {
  static focusPageOnCancel = false;

  protected override _Content = this.getByRef('Content')!;
  private _ResetPin = this._Content.getByRef('ResetPin')!;

  static override _template(): Lightning.Component.Template<EnterParentalPinTemplateSpec> {
    const { Content, ...rest } = super._template();
    return {
      Content: {
        ...Content,
        ResetPin: {
          flexItem: { marginTop: RESET_PIN_MARGIN_TOP },
          type: ResetPinButton,
          signals: {
            $onHover: true,
          },
          zIndex: constants.ui.zIndex.foregroundLayer + 2,
        },
      },
      ...rest,
    };
  }

  protected override update() {
    this.title = translate('ParentalPin.enterPin.title');
    this.description = translate('ParentalPin.enterPin.description');

    if (EnterParentalPin.focusPageOnCancel) {
      this._ResetPin.patch({ visible: false });
    } else {
      this._ResetPin.patch({ visible: true });
    }

    super.update();
  }

  override getCancelLabel() {
    if (EnterParentalPin.focusPageOnCancel) {
      return translate('ParentalPin.cancel');
    } else {
      return translate('ParentalPin.enterPin.exitApp');
    }
  }

  override _handleDown() {
    if (!EnterParentalPin.focusPageOnCancel) {
      this._setState('ResetPin');
    }
  }

  protected override closeWidget() {
    AppData?.modalService.close();
    this.resetStates();
  }

  override $onHover(target: unknown) {
    super.$onHover(target);
    switch (target) {
      case this._ResetPin:
        this._setState('ResetPin');
        return;
    }
  }

  override $validate() {
    const isValid = AppData!.parentalPinService.validatePin(this.pin);

    if (!isValid) {
      this.handleInvalidPin();
    } else {
      this.onValidate?.();
      this.closeWidget();
    }
  }

  override $cancel() {
    if (EnterParentalPin.focusPageOnCancel) {
      this.resetStates();
      this.closeWidget();
    } else {
      AppData?.modalService.open('ExitModal');
    }
  }

  static override _states(): Lightning.Component.Constructor[] {
    return [
      ...super._states(),
      class ResetPin extends this {
        override _getFocused() {
          return this._ResetPin;
        }

        override _handleEnter() {
          this.resetStates();

          AppData?.modalService.open('ResetParentalPin', {
            data: { onValidate: this.onValidate },
          });
        }

        override _handleUp() {
          this._setState('ActionsState');
        }
      },
    ];
  }
}
